import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, CheckIcon, Loader2Icon, MailCheckIcon } from 'lucide-react';
import { AuthLayout } from '../../layouts/AuthLayout';
import { useAuth } from '../../contexts/AuthContext';
import { updateOrganization } from '../../services/organizationService';
import { SECTOR_OPTIONS } from '../../data/sectors';
import { BrandLogo } from '../../components/shared/BrandLogo';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  repeatPassword: string;
  companyName: string;
  sector: string;
  primaryColor: string;
  secondaryColor: string;
}
const INITIAL: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  repeatPassword: '',
  companyName: '',
  sector: '',
  primaryColor: '#3D8BFF',
  secondaryColor: '#0B1F4D'
};
const STEPS = ['Tu cuenta', 'Tu empresa', 'Logotipo', 'Colores', 'Sector'];
const CREATING_STEPS = ['Creando tu cuenta', 'Creando el espacio de trabajo de tu empresa', 'Preparando los estados iniciales del pipeline'];
const PRESET_COLORS = ['#3D8BFF', '#0B1F4D', '#16A34A', '#7C3AED', '#F97316', '#0EA5E9'];
export function Register() {
  const {
    register,
    loading
  } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState<FormState>(INITIAL);
  const [logo, setLogo] = useState<string | undefined>(undefined);
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);
  /** La cuenta se ha creado pero necesita confirmarse por email. */
  const [confirmEmail, setConfirmEmail] = useState(false);
  function set(key: keyof FormState, value: string) {
    setValues(prev => ({
      ...prev,
      [key]: value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: ''
    }));
  }
  function validateStep(index: number): boolean {
    const next: Record<string, string> = {};
    if (index === 0) {
      if (!values.firstName.trim()) next.firstName = 'Introduce tu nombre.';
      if (!values.lastName.trim()) next.lastName = 'Introduce tus apellidos.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) next.email = 'Introduce un email válido.';
      if (values.password.length < 8) next.password = 'Usa al menos 8 caracteres.';
      if (values.password !== values.repeatPassword) next.repeatPassword = 'Las contraseñas no coinciden.';
      if (!terms) next.terms = 'Debes aceptar los términos.';
    }
    if (index === 1 && !values.companyName.trim()) next.companyName = 'Introduce el nombre de tu empresa.';
    if (index === 4 && !values.sector) next.sector = 'Selecciona un sector.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  async function finish() {
    if (!validateStep(4)) return;
    setCreating(true);
    try {
      const session = await register({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        companyName: values.companyName,
        sector: values.sector,
        primaryColor: values.primaryColor,
        secondaryColor: values.secondaryColor,
        logoUrl: logo
      });

      // Sin sesión: la cuenta debe confirmarse por email y el espacio de
      // trabajo se creará en el primer acceso.
      if (!session) {
        setCreating(false);
        setConfirmEmail(true);
        return;
      }
      await updateOrganization(session.organizationId, {
        logoUrl: logo,
        brand: {
          primaryColor: values.primaryColor,
          secondaryColor: values.secondaryColor,
          fontFamily: 'Inter',
          logoUrl: logo
        }
      });
      navigate('/app/dashboard');
    } catch (err) {
      setCreating(false);
      setStep(0);
      setErrors({
        email: err instanceof Error ? err.message : 'No se ha podido crear la cuenta.'
      });
    }
  }
  if (confirmEmail) {
    return <AuthLayout title="Confirma tu correo" subtitle={`Hemos enviado un enlace de confirmación a ${values.email}.`} footer={<span className="text-muted-foreground" data-id="element-1526" data-mp="17ianz75oo70e">
            ¿Ya lo has confirmado?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline" data-id="element-1527" data-mp="1mfba8v53v9rh">
              Iniciar sesión
            </Link>
          </span>} data-id="element-1525" data-mp="ynvzqf36dm55">
        <div className="space-y-5 text-sm leading-relaxed text-muted-foreground" data-id="element-1528" data-mp="1hhjf7s95z7j2">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10" data-id="element-1529" data-mp="3o7plxch23hzg">
            <MailCheckIcon className="h-6 w-6 text-primary" aria-hidden="true" data-id="element-1530" data-mp="3j8msyqecp27u" />
          </span>
          <p data-id="element-1531" data-mp="1yqlovqcd3yx2">
            Abre el enlace del correo para activar tu cuenta. Al iniciar sesión
            por primera vez crearemos automáticamente el espacio de trabajo de{' '}
            <strong className="text-foreground" data-id="element-1532" data-mp="1p09725vxmjum">{values.companyName}</strong> con
            tu logotipo, tus colores, los estados del embudo y tu primera landing.
          </p>
          <p data-id="element-1533" data-mp="1twhjvcmnq0ym">
            Si no lo encuentras, revisa la carpeta de spam. El enlace caduca en
            24 horas.
          </p>
          <Link to="/login" data-id="element-1534" data-mp="1k1636oroujo8">
            <Button size="lg" className="w-full" data-id="element-1535" data-mp="1k27orn6byok7">
              Ir a iniciar sesión
            </Button>
          </Link>
        </div>
      </AuthLayout>;
  }
  if (creating) {
    return <AuthLayout title="Estamos creando tu CRM" subtitle={`Espacio de trabajo de ${values.companyName}`} data-id="element-1536" data-mp="1b114d30k8tlo">
        <ul className="space-y-4" data-id="element-1537" data-mp="30g7lyqdsk03r">
          {CREATING_STEPS.map((label, index) => <li key={label} className="flex items-center gap-3 text-sm" data-id="element-1538" data-mp="35eituvkj00jc" data-mpi={index}>
              {index === 0 ? <CheckCircle2Icon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-1539" data-mp="33wk9mk1gnmkn" /> : <Loader2Icon className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" data-id="element-1540" data-mp="33uh7j1n1a2zk" />}
              <span className="text-foreground" data-id="element-1541" data-mp="2z03idgyyss3c">{label}</span>
            </li>)}
        </ul>
      </AuthLayout>;
  }
  const stepper = <ol className="mb-9 flex items-center gap-2" aria-label="Progreso del registro" data-id="element-1542" data-mp="2p02xhqlg63x1">
      {STEPS.map((label, index) => <li key={label} className="flex flex-1 items-center gap-2" data-id="element-1543" data-mp="3mnl5gz7ab1zz" data-mpi={index}>
          <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-full text-[11px] font-semibold transition-colors ${index < step ? 'bg-primary/10 text-primary' : index === step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`} data-id="element-1544" data-mp="3ho8go8wurkur">
            {index < step ? <CheckIcon className="h-3.5 w-3.5" data-id="element-1545" data-mp="98f0xwacider" /> : index + 1}
          </span>
          {index < STEPS.length - 1 && <span className={`h-px flex-1 ${index < step ? 'bg-primary/40' : 'bg-border'}`} aria-hidden="true" data-id="element-1546" data-mp="4hyoofi9gxtz" />}
        </li>)}
    </ol>;
  return <AuthLayout title={step === 0 ? 'Crea el CRM de tu empresa' : `Paso ${step + 1} · ${STEPS[step]}`} subtitle={step === 0 ? 'Tu espacio de trabajo se crea automáticamente y solo tu equipo podrá verlo.' : 'Personaliza el espacio de trabajo de tu empresa. Podrás cambiarlo cuando quieras.'} footer={step === 0 ? <span className="text-muted-foreground" data-id="element-1548" data-mp="qqx1kamc1cmh">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline" data-id="element-1549" data-mp="vo5oeoife4u2">
              Iniciar sesión
            </Link>
          </span> : undefined} data-id="element-1547" data-mp="cre3ybr5djof">
      {stepper}

      <div className="space-y-5" data-id="element-1550" data-mp="14h0iqn6cug52">
        {step === 0 && <>
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1551" data-mp="14jlk7b2qqu2a">
              <Input id="firstName" label="Nombre" value={values.firstName} error={errors.firstName} onChange={e => set('firstName', e.target.value)} data-id="element-1552" data-mp="19fk68s0ljzzj" />
              <Input id="lastName" label="Apellidos" value={values.lastName} error={errors.lastName} onChange={e => set('lastName', e.target.value)} data-id="element-1553" data-mp="1edvi18agckke" />
            </div>
            <Input id="email" label="Email" type="email" autoComplete="email" value={values.email} error={errors.email} onChange={e => set('email', e.target.value)} data-id="element-1554" data-mp="24o6npp7wii2p" />
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1555" data-mp="2dqo8s0btl4o4">
              <Input id="password" label="Contraseña" type="password" autoComplete="new-password" value={values.password} error={errors.password} onChange={e => set('password', e.target.value)} data-id="element-1556" data-mp="2do1znw9a6h3s" />
              <Input id="repeatPassword" label="Repetir contraseña" type="password" autoComplete="new-password" value={values.repeatPassword} error={errors.repeatPassword} onChange={e => set('repeatPassword', e.target.value)} data-id="element-1557" data-mp="1kzeavqpbp9lh" />
            </div>
            <div data-id="element-1558" data-mp="1q0lfxi02zuog">
              <Checkbox id="terms" label="Acepto los términos y la política de privacidad." checked={terms} onChange={e => {
            setTerms(e.target.checked);
            setErrors(prev => ({
              ...prev,
              terms: ''
            }));
          }} data-id="element-1559" data-mp="1q021ib9n3of6" />
              {errors.terms && <p role="alert" className="mt-2 text-sm font-medium text-red-600" data-id="element-1560" data-mp="38yq5x2qso4wp">
                  {errors.terms}
                </p>}
            </div>
          </>}

        {step === 1 && <Input id="companyName" label="Nombre de la empresa" placeholder="Mi Empresa S.L." value={values.companyName} error={errors.companyName} onChange={e => set('companyName', e.target.value)} data-id="element-1561" data-mp="2zzmm1wjszlh5" />}

        {step === 2 && <>
            <LogoUploader value={logo} onChange={setLogo} data-id="element-1562" data-mp="3so0lmgqjiput" />
            <div className="rounded-xl border border-border bg-surface p-4" data-id="element-1563" data-mp="k82jebstdjf">
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground" data-id="element-1564" data-mp="3ipbe9hw9snst">
                Vista previa
              </p>
              <div className="flex items-center gap-3 rounded-lg bg-background px-4 py-3" data-id="element-1565" data-mp="3nn4hc22a11ov">
                <BrandLogo src={logo} size={22} data-id="element-1566" data-mp="2794i6tb99g2o" />
                <span className="text-sm text-muted-foreground" data-id="element-1567" data-mp="27cimw87e61da">
                  {values.companyName || 'Tu empresa'}
                </span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground" data-id="element-1568" data-mp="2n05qn8kujn08">
              Puedes continuar sin logotipo y añadirlo más adelante desde
              Configuración → Identidad de empresa.
            </p>
          </>}

        {step === 3 && <>
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1569" data-mp="2i848x74wt42t">
              <Input id="primary" label="Color principal" type="color" value={values.primaryColor} onChange={e => set('primaryColor', e.target.value)} data-id="element-1570" data-mp="2i6t2ezk2xi80" />
              <Input id="secondary" label="Color secundario" type="color" value={values.secondaryColor} onChange={e => set('secondaryColor', e.target.value)} data-id="element-1571" data-mp="28aq464scm3qc" />
            </div>
            <div data-id="element-1572" data-mp="1z9jkxnybeete">
              <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1573" data-mp="1z901u3wxhkds">Sugerencias</p>
              <div className="flex flex-wrap gap-2" data-id="element-1574" data-mp="3oozjk9em4i51">
                {PRESET_COLORS.map(color => <button key={color} type="button" aria-label={`Usar ${color} como color principal`} onClick={() => set('primaryColor', color)} style={{
              backgroundColor: color
            }} className={`h-9 w-9 rounded-full border-2 transition-transform hover:scale-105 ${values.primaryColor === color ? 'border-foreground' : 'border-transparent'}`} data-id="element-1575" data-mp="3omwq1fe946vk" />)}
              </div>
            </div>
            <div className="rounded-xl border border-border p-5" style={{
          borderColor: `${values.primaryColor}33`
        }} data-id="element-1576" data-mp="1mzk66hhdud9n">
              <p className="text-xs uppercase tracking-wider text-muted-foreground" data-id="element-1577" data-mp="20pxdqiqd3pyg">
                Vista previa
              </p>
              <p className="mt-2 text-lg font-semibold" style={{
            color: values.secondaryColor
          }} data-id="element-1578" data-mp="1vrm5udjmnpiv">
                {values.companyName || 'Tu empresa'}
              </p>
              <span className="mt-4 inline-flex h-10 items-center rounded-lg px-5 text-sm font-medium text-white" style={{
            backgroundColor: values.primaryColor
          }} data-id="element-1579" data-mp="25ql2exr4bupi">
                Botón principal
              </span>
            </div>
          </>}

        {step === 4 && <Select id="sector" label="Sector de la empresa" placeholder="Selecciona un sector" options={SECTOR_OPTIONS} value={values.sector} error={errors.sector} onChange={e => set('sector', e.target.value)} data-id="element-1580" data-mp="111hhsu2lp1xp" />}
      </div>

      <div className="mt-9 flex items-center justify-between gap-3" data-id="element-1581" data-mp="19zjc6qrtwwc0">
        <Button variant="ghost" disabled={step === 0} leftIcon={<ArrowLeftIcon className="h-4 w-4" data-id="element-1583" data-mp="1jz0hh736587g" />} onClick={() => setStep(s => Math.max(0, s - 1))} data-id="element-1582" data-mp="1f0yxmjxx54ux">
          Anterior
        </Button>
        {step < STEPS.length - 1 ? <Button size="lg" rightIcon={<ArrowRightIcon className="h-4 w-4" data-id="element-1585" data-mp="1ny9ag9qdx21y" />} onClick={() => {
        if (validateStep(step)) setStep(s => s + 1);
      }} data-id="element-1584" data-mp="1nz2kpkojy9ou">
            Continuar
          </Button> : <Button size="lg" className="ava-glow" loading={loading} onClick={finish} data-id="element-1586" data-mp="1xp3z1kr3sdqk">
            Crear mi CRM
          </Button>}
      </div>
    </AuthLayout>;
}